/**
 * `/audio` — the Rotom Phone's Music app (plan §9, §9.11): where a player lands from the link `/audio` prints
 * in game, presses one button, and hears their soundtrack follow them through the Nexus, into battles and
 * home to their base.
 *
 * WHO IT IS FOR. Players first — "normal people should be straight on the audio menu" — so it reads like a
 * player's app: no tiers, no tables, no staff vocabulary. Staff reach it from the phone too (a staff sign-in
 * names no player, so they listen by typing `/audio` like everybody else).
 *
 * WHAT IT DRAWS, AND WHAT IT DOES NOT OWN. The engine, the socket and the service worker live in the phone's
 * shell (`AudioCompanionProvider`), so this app is a view of them: leaving it for Stats or the Map changes
 * nothing that plays. Who you are comes from the sidecar (the claim, or the renewal it reads); where you are
 * from the game server through the socket; what plays is decided by the shared resolution functions and
 * drawn from the engine's snapshot. Nothing here asks the read plane anything — it works on a deployment with
 * no Postgres behind it.
 */
import { useEffect, useRef } from 'react';
import { Link } from 'react-router';

import { useSession } from '../auth/SessionProvider';
import { SignInButton } from '../components/SessionBar';
import keeper192 from '../assets/keeper-192.webp';
import { useAudioCompanion } from './AudioCompanionProvider';
import { CREDITS_URL, LIBRARY, LIBRARY_KIND } from './library';
import { AreaName, PlayerHead, Visualiser, VolumePanel, plainAreaName } from './parts';
import { moodOf, statusOf } from './status';
import type { CompanionState } from './useAudioCompanion';

import './audio.css';

export function AudioPage(): React.JSX.Element {
  const { state, staff, enable, setVolumes, setMuted, signOut, clock, tickSink, engine } = useAudioCompanion();
  const { phase, identity, snapshot } = state;
  const area = snapshot.area;
  const vehicle = snapshot.vehicle;
  const mood = moodOf(state);
  const status = statusOf(state);
  const playing = state.enabled && snapshot.state === 'playing';
  // The visualiser draws the whole mix, so the ride's sound alone is enough to light it.
  const audible = playing || (state.enabled && vehicle.state === 'playing');

  // The live position, written straight to the DOM on each tick — four times a second without a render.
  // The e2e suite reads it to prove two players are on one beat.
  const positionRef = useRef<HTMLSpanElement | null>(null);
  useEffect(() => {
    tickSink.current = (sample) => {
      const element = positionRef.current;
      if (element === null) return;
      element.dataset['positionMs'] = String(Math.round(sample.positionMs));
      element.dataset['expectedMs'] = sample.expectedMs === null ? '' : String(Math.round(sample.expectedMs));
      element.dataset['atMs'] = String(sample.atMs);
      element.dataset['durationMs'] = sample.durationMs === null ? '' : String(Math.round(sample.durationMs));
    };
    return () => {
      tickSink.current = null;
    };
  }, [tickSink]);

  useEffect(() => {
    const name = area === null ? null : plainAreaName(area.displayName);
    globalThis.document.title = name !== null && playing ? `${name} — Rotom Phone` : 'Music — Rotom Phone';
  }, [area, playing]);

  const sync = snapshot.instance === null ? null : clock();

  return (
    <div className="au-page" data-testid="page-audio" data-phase={phase} data-mood={mood}>
      {state.reloadRequired && (
        <div className="au-banner dc-frame" role="alert" data-testid="audio-reload">
          <span>A new version of this page is out.</span>
          <button type="button" className="dc-button" onClick={() => globalThis.location.reload()}>
            Reload
          </button>
        </div>
      )}

      {(phase === 'checking' || phase === 'claiming') && (
        <section className="au-card dc-frame au-checking" data-testid="audio-checking" aria-busy="true">
          <p>{phase === 'claiming' ? 'Opening your link…' : 'Looking for your audio session…'}</p>
        </section>
      )}

      {phase === 'signed-out' && (
        <>
          <WayIn reason={state.signedOutReason} staff={staff} />
          <KeeperCard welcome />
        </>
      )}

      {phase === 'ready' && identity !== null && (
        <>
          <section className={`au-card dc-frame au-player au-tone-${status.tone}`} data-testid="audio-player" aria-label="You">
            <PlayerHead uuid={identity.uuid} skin={identity.skin} size={56} />
            <div className="au-player-text">
              <p className="au-player-name" data-testid="audio-player-name">
                {identity.name}
              </p>
              <p className="au-status" data-testid="audio-status" data-link={state.link} data-heard={state.heard ? 'yes' : 'no'} data-online={state.context?.online === true ? 'yes' : 'no'}>
                <span className="au-status-dot" aria-hidden="true" />
                <span className="au-status-label">{status.label}</span>
              </p>
              <p className="au-status-detail">{status.detail}</p>
            </div>
          </section>

          <section
            className={`au-card dc-frame dc-frame-copper au-now au-now-${mood}`}
            aria-labelledby="au-now-title"
            data-testid="now-playing"
            data-state={snapshot.state}
            data-area={area?.id ?? ''}
            data-track={snapshot.trackId ?? ''}
            data-instance={snapshot.instance?.id ?? ''}
            data-instance-started={snapshot.instance?.startedAtMs ?? ''}
            data-starts={snapshot.starts}
            data-vehicle-state={vehicle.state}
            data-vehicle-area={vehicle.area?.id ?? ''}
            data-vehicle-track={vehicle.trackId ?? ''}
            data-vehicle-starts={vehicle.starts}
            data-enabled={state.enabled ? 'yes' : 'no'}
          >
            <p id="au-now-title" className="au-card-title">
              {state.enabled ? 'Now playing' : 'Ready when you are'}
            </p>
            <p className="au-area" data-testid="now-area">
              {area === null ? (state.context?.online === true ? 'Quiet here' : '—') : <AreaName source={area.displayName} />}
            </p>
            {snapshot.track !== null && (
              <p className="au-track" data-testid="now-track">
                <span className="au-track-title">{snapshot.track.title}</span>
                {snapshot.track.artist !== '' && <span className="au-track-artist"> · {snapshot.track.artist}</span>}
                {snapshot.track.license !== '' && (
                  <span className="au-track-license">
                    {' · '}
                    {snapshot.track.sourceUrl === '' ? (
                      snapshot.track.license
                    ) : (
                      <a href={snapshot.track.sourceUrl} target="_blank" rel="noreferrer noopener">
                        {snapshot.track.license}
                      </a>
                    )}
                  </span>
                )}
              </p>
            )}
            {snapshot.instance !== null && (
              <p className="au-shared" data-testid="now-shared">
                <span className="au-chip">Shared</span> Everyone here hears this on one beat
                {sync !== null && sync.roundTripMs !== null ? ` · in sync to ±${Math.max(1, Math.round(sync.roundTripMs / 2))} ms` : ''}
              </p>
            )}
            {vehicle.area !== null && (
              <p className="au-vehicle" data-testid="now-vehicle">
                <span className="au-chip">Vehicle</span> <AreaName source={vehicle.area.displayName} />
              </p>
            )}

            <Visualiser source={engine?.analyser ?? null} active={audible} intense={mood === 'battle'} />

            {!state.enabled && (
              <div className="au-enable">
                <button type="button" className="dc-button dc-button-big" onClick={() => void enable()} disabled={!state.supported} data-testid="audio-enable">
                  {state.enabledBefore ? '▶ Resume audio' : '▶ Enable audio'}
                </button>
                <p className="au-fineprint">
                  {state.supported
                    ? 'Browsers only play sound after you press something. One press, and the music follows you — into every app on this phone.'
                    : 'This browser cannot play audio from a web page. Try a current Chrome, Firefox, Edge or Safari.'}
                </p>
              </div>
            )}
            <span ref={positionRef} hidden data-testid="audio-position" />
          </section>

          <VolumePanel volumes={state.prefs.volumes} muted={state.prefs.muted} onVolumes={setVolumes} onMuted={setMuted} />
          <section className="au-card dc-frame au-tip" aria-labelledby="au-tip-title">
            <h2 id="au-tip-title" className="au-card-title">
              Tip
            </h2>
            <p>
              Turn Minecraft&rsquo;s own music off — <b>Options › Music &amp; Sounds › Music</b> — so the game&rsquo;s tracks do not play over this phone.
            </p>
          </section>
          <KeeperCard />
          <p className="au-signout-row">
            <button type="button" className="dc-linkish au-signout" onClick={() => void signOut()} data-testid="audio-sign-out">
              Sign out on this device
            </button>
          </p>
        </>
      )}

      <Credits />
    </div>
  );
}

/** Signed out: how to get in. "Type /audio in game", in three steps, and the staff door beside it. */
function WayIn({ reason, staff }: { readonly reason: CompanionState['signedOutReason']; readonly staff: boolean }): React.JSX.Element {
  const { session } = useSession();
  // A Discord account in the guild with no binding yet: signed in, but it names no player.
  const unlinked = session !== null && session.claims.role === 'unlinked';
  const notice =
    reason === 'used_or_expired'
      ? 'That link has been used already, or it is more than a few minutes old. Type /audio in game for a fresh one.'
      : reason === 'renewal_ended'
        ? 'Your audio session on this device has ended. Type /audio in game to start a new one.'
        : reason === 'unreachable'
          ? 'The audio server could not be reached just now. Check your connection, then reload the page.'
          : null;
  return (
    <section className="au-card dc-frame au-wayin" aria-labelledby="au-wayin-title" data-testid="audio-way-in">
      <h2 id="au-wayin-title" className="au-card-title">
        Get your music in three steps
      </h2>
      {notice !== null && (
        <p className="au-notice" role="alert" data-testid="audio-way-in-notice">
          {notice}
        </p>
      )}
      <ol className="au-steps">
        <li>
          <span>
            <b>Join the server</b> in Minecraft, as you always do.
          </span>
        </li>
        <li>
          <span>
            <b>
              Type <kbd>/audio</kbd> in chat.
            </b>{' '}
            You get a link that works only for you, for a few minutes.
          </span>
        </li>
        <li>
          <span>
            <b>Open the link</b> and press <i>Enable audio</i>. This browser remembers you for weeks, so you only do this once per device.
          </span>
        </li>
      </ol>
      <p className="au-hear">
        <span>Every district of the Nexus</span>
        <span>A theme for every battle</span>
        <span>Chimes for mail, catches and level-ups</span>
        <span>Your own base</span>
      </p>
      <p className="au-fineprint">
        {staff ? (
          'You are signed in as staff. A staff sign-in names no player, so type /audio in game to listen as your character.'
        ) : unlinked ? (
          <>
            Your Discord account is not linked to a Minecraft account yet. Music does not need it — type <kbd>/audio</kbd> in game. To see your own
            homes and rewards on this phone as well, <Link to="/me">link it in Stats</Link>.
          </>
        ) : (
          'No account, no password and no Discord needed: being logged in to the game is the proof.'
        )}
      </p>
      {/* The door is for somebody with no session; anybody signed in already has Sign out in Settings. */}
      {session === null && (
        <p className="au-staff-door">
          Staff? <SignInButton returnTo="/" compact testId="audio-staff-sign-in" />
        </p>
      )}
    </section>
  );
}

function KeeperCard({ welcome = false }: { readonly welcome?: boolean }): React.JSX.Element {
  return (
    <aside className="au-card dc-frame au-keeper" aria-label="Dimension Keeper">
      <img src={keeper192} width={56} height={56} alt="" className="au-keeper-avatar" />
      <div>
        <p className="au-keeper-name">Dimension Keeper</p>
        <p className="au-keeper-line">
          {welcome
            ? 'Welcome to the Nexus. Once you are in, keep this phone open while you play — I will change the music as you go.'
            : 'Keep this phone open while you play. I swap the music as you cross the Nexus, start the right theme when a battle begins, and chime when your mail arrives.'}
        </p>
      </div>
    </aside>
  );
}

function Credits(): React.JSX.Element {
  const tracks = Object.entries(LIBRARY).filter(([id]) => !id.startsWith('voice/'));
  return (
    <details className="au-credits" data-testid="audio-credits">
      <summary>
        Music credits <span className="au-dim">({tracks.length} tracks)</span>
      </summary>
      {LIBRARY_KIND === 'dev' && <p className="au-fineprint">This build carries the generated development library: placeholder tones, CC0.</p>}
      <ul>
        {tracks.map(([id, entry]) => (
          <li key={id}>
            <b>{entry.title}</b>
            {entry.artist !== '' && <> — {entry.artist}</>}
            {entry.license !== '' && (
              <>
                {' · '}
                {entry.sourceUrl === '' ? (
                  entry.license
                ) : (
                  <a href={entry.sourceUrl} target="_blank" rel="noreferrer noopener">
                    {entry.license}
                  </a>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
      {CREDITS_URL !== null && (
        <p>
          <a href={CREDITS_URL} target="_blank" rel="noreferrer" data-testid="audio-credits-full">
            Full credits
          </a>
        </p>
      )}
    </details>
  );
}
